import React, { useCallback, useEffect } from 'react'
import { useParams, useNavigate, useLocation, Navigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useIsUser } from 'src/pages/Identity/Login'
import {
  Box,
  Button,
  Grid,
  Typography,
  Chip,
  Stack,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DescriptionIcon from '@mui/icons-material/Description'
import posthog from 'posthog-js'
import PersonIcon from '@mui/icons-material/Person'
import ContactMailIcon from '@mui/icons-material/ContactMail'
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'
import SchoolIcon from '@mui/icons-material/School'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import WorkIcon from '@mui/icons-material/Work'
import { sanitize } from 'dompurify'
import ProfileSkeleton from 'src/components/Profile/ProfileSkeleton'
import { hostAddress } from 'data/helpers'
import { fetchGetCandidate } from 'src/redux/candidate'
import EmployerCandidateHeader from './EmployerCandidateHeader'
import CandidateDetailSection from './CandidateDetailSection'
import {
  formatYearsExperience,
  formatBoardEligibility,
  formatSearchStatus,
  formatAvailabilityDate,
  formatSpecialties,
  formatLanguages,
  formatVisas,
  formatPreferredStates,
  formatRelocationPreference,
  formatPracticeTypes,
  formatAreaTypes,
  formatAcademicPreference,
  formatGlobalRegions,
  formatLicensedStates,
  formatMemberDate,
} from './candidateDisplayHelpers'

const DetailRow = ({ label, value }) => {
  if (!value) return null
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant='caption' color='text.secondary'>
        {label}
      </Typography>
      <Typography variant='body2'>{value}</Typography>
    </Box>
  )
}

const ChipList = ({ label, items }) => {
  if (!items) return null
  const list = items.split(', ')
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Stack direction='row' spacing={0.5} flexWrap='wrap' gap={0.5}>
        {list.map(item => (
          <Chip key={item} label={item} size='small' variant='outlined' />
        ))}
      </Stack>
    </Box>
  )
}

const EmployerCandidateProfilePage = () => {
  const { id } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { isEmployerUser } = useIsUser()
  const candidate = useSelector(state => state.candidate)

  const handleResumeClick = useCallback(() => {
    if (!candidate.resume?.key) return
    posthog.capture('employer_candidate_resume_downloaded', { candidate_id: id })
    const { key, fileName } = candidate.resume
    fetch(hostAddress(`/api/storage/resume?key=${key}&fileName=${fileName}`), {
      method: 'GET',
      headers: new Headers({ 'content-type': 'application/json' }),
    }).then(response => response.blob().then(blob => window.open(URL.createObjectURL(blob))))
  }, [candidate.resume, id])

  useEffect(() => {
    if (isEmployerUser) {
      dispatch(fetchGetCandidate(id))
    }
  }, [dispatch, id, isEmployerUser])

  if (!isEmployerUser) {
    return <Navigate to='/' replace />
  }

  if (candidate.loading || String(candidate.id) !== String(id)) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, py: 3 }}>
        <ProfileSkeleton />
      </Box>
    )
  }

  const residencyDateRange = candidate.residencyStart || candidate.residencyEnd
    ? [
        candidate.residencyStart
          ? new Date(candidate.residencyStart).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
          : null,
        candidate.residencyEnd
          ? new Date(candidate.residencyEnd).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
          : null,
      ].filter(Boolean).join(' – ')
    : null

  const residencyDisplay = [
    candidate.residency,
    residencyDateRange,
  ]
    .filter(Boolean)
    .join(' — ')

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, py: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(location.state?.from || '/jobs')}
        sx={{ mb: 2 }}
      >
        Back
      </Button>

      <EmployerCandidateHeader candidate={candidate} />

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Main column */}
        <Grid item xs={12} md={8}>
          {/* About */}
          {(candidate.about || candidate.aboutJob) && (
            <CandidateDetailSection
              icon={<PersonIcon color='action' />}
              title='About'
            >
              {candidate.about && (
                <Box
                  sx={{ mb: candidate.aboutJob ? 2 : 0 }}
                  dangerouslySetInnerHTML={{ __html: sanitize(candidate.about) }}
                />
              )}
              {candidate.aboutJob && (
                <>
                  <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 0.5 }}>
                    What I'm Looking For
                  </Typography>
                  <Box dangerouslySetInnerHTML={{ __html: sanitize(candidate.aboutJob) }} />
                </>
              )}
            </CandidateDetailSection>
          )}

          {/* Contact */}
          {(candidate.showEmail || candidate.showPhone) && (
            <CandidateDetailSection
              icon={<ContactMailIcon color='action' />}
              title='Contact'
            >
              {candidate.showEmail && (
                <DetailRow label='Email' value={candidate.email} />
              )}
              {candidate.showPhone && (
                <DetailRow label='Phone' value={candidate.phone} />
              )}
            </CandidateDetailSection>
          )}

          {/* Professional Details */}
          <CandidateDetailSection
            icon={<MedicalServicesIcon color='action' />}
            title='Professional Details'
          >
            <DetailRow label='Candidate Type' value={candidate.candidateType?.name} />
            <ChipList label='Specialties' items={formatSpecialties(candidate.specialties)} />
            <ChipList
              label='Subspecialties'
              items={formatSpecialties(candidate.subspecialties)}
            />
            <DetailRow
              label='Board Eligibility'
              value={formatBoardEligibility(candidate.boardEligibility)}
            />
            <DetailRow label='NPI Number' value={candidate.npiNumber} />
            {candidate.resume?.key && (
              <Box sx={{ mb: 1 }}>
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 0.5 }}>
                  Resume
                </Typography>
                <Button
                  startIcon={<DescriptionIcon />}
                  variant='outlined'
                  size='small'
                  onClick={handleResumeClick}
                >
                  View Resume
                </Button>
              </Box>
            )}
            <DetailRow
              label='Years of Experience'
              value={formatYearsExperience(candidate.yearsExperience)}
            />
            <ChipList
              label='Licensed States'
              items={formatLicensedStates(candidate.licensedStates)}
            />
          </CandidateDetailSection>

          {/* Background */}
          <CandidateDetailSection
            icon={<SchoolIcon color='action' />}
            title='Background'
          >
            <DetailRow label='Residency' value={residencyDisplay || null} />
            <DetailRow label='Languages' value={formatLanguages(candidate.candidateLanguages)} />
            <ChipList label='Visa Types' items={formatVisas(candidate.visas)} />
          </CandidateDetailSection>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Availability */}
          <CandidateDetailSection
            icon={<EventAvailableIcon color='action' />}
            title='Availability'
          >
            <DetailRow
              label='Search Status'
              value={formatSearchStatus(candidate.searchStatus)}
            />
            <DetailRow
              label='Available'
              value={formatAvailabilityDate(candidate.availabilityDate)}
            />
            <DetailRow
              label='Member Since'
              value={formatMemberDate(candidate.createdAt)}
            />
          </CandidateDetailSection>

          {/* Location Preferences */}
          <CandidateDetailSection
            icon={<LocationOnIcon color='action' />}
            title='Location Preferences'
          >
            <DetailRow label='Preferred City' value={candidate.preferredCity} />
            <ChipList
              label='Preferred States'
              items={formatPreferredStates(candidate.preferredStates)}
            />
            <DetailRow
              label='Relocation'
              value={formatRelocationPreference(candidate.relocationPreference)}
            />
            <ChipList
              label='Preferred Area Types'
              items={formatAreaTypes(candidate.preferredAreaTypes)}
            />
          </CandidateDetailSection>

          {/* Job Preferences */}
          <CandidateDetailSection
            icon={<WorkIcon color='action' />}
            title='Job Preferences'
          >
            <ChipList
              label='Practice Types'
              items={formatPracticeTypes(candidate.preferredPracticeTypes)}
            />
            <DetailRow
              label='Academic Preference'
              value={formatAcademicPreference(candidate.academicPreference)}
            />
            <ChipList
              label='Global Regions'
              items={formatGlobalRegions(candidate.preferredGlobalRegions)}
            />
          </CandidateDetailSection>
        </Grid>
      </Grid>
    </Box>
  )
}

export default EmployerCandidateProfilePage
